import { createBrowserRouter, Navigate, Outlet } from "react-router";
import { RouteErrorBoundary } from "@/shared/components/RouteErrorBoundary";
import { FrontChatController } from "@/shared/components/FrontChatController";
import { MarketingHome } from "@/routes/marketing/Home";
import { CookiePolicy } from "@/routes/marketing/CookiePolicy";
import { TermsOfService } from "@/routes/marketing/TermsOfService";
import { PrivacyPolicy } from "@/routes/marketing/PrivacyPolicy";
import { AppLayout } from "@/routes/app/layout";
import { RequireAuth } from "@/routes/app/RequireAuth";
import { RedirectIfAuthed } from "@/routes/auth/RedirectIfAuthed";
import { Login } from "@/routes/auth/Login";
import { Signup } from "@/routes/auth/Signup";
import { PasswordRecover } from "@/routes/auth/PasswordRecover";
import { PasswordReset } from "@/routes/auth/PasswordReset";
import { ContactsList } from "@/routes/app/contacts/list";
import { ExportPage } from "@/routes/app/contacts/export";
import { ContactNew } from "@/routes/app/contacts/new";
import { ContactImport } from "@/routes/app/contacts/import";
import { CollectContacts } from "@/routes/app/contacts/collect";
import { ContactDetail } from "@/routes/app/contacts/detail";
import { ContactEdit } from "@/routes/app/contacts/edit";
import { HouseholdsList } from "@/routes/app/households/list";
import { HouseholdNew } from "@/routes/app/households/new";
import { HouseholdDetailPage } from "@/routes/app/households/detail";
import { HouseholdEdit } from "@/routes/app/households/edit";
import { ProfilePage } from "@/routes/app/profile/page";
import { RequireAdmin } from "@/routes/app/admin/RequireAdmin";
import { AdminDashboard } from "@/routes/app/admin/dashboard";
import { AdminUsersList } from "@/routes/app/admin/users/list";
import { AdminUserNew } from "@/routes/app/admin/users/new";
import { AdminUserDetail } from "@/routes/app/admin/users/detail";
import { CollectShare } from "@/routes/public/CollectShare";

export const router = createBrowserRouter([
  {
    // Pathless root route: a single errorElement covers every page. With a data
    // router, route render/loader/action errors are caught here (a boundary
    // around <RouterProvider> would not see them). The Front chat controller
    // mounts here so it sees every client navigation via `useLocation`.
    element: (
      <>
        <FrontChatController />
        <Outlet />
      </>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: "/", element: <MarketingHome /> },
      { path: "/cookie-policy", element: <CookiePolicy /> },
      { path: "/terms-of-service", element: <TermsOfService /> },
      { path: "/privacy-policy", element: <PrivacyPolicy /> },
      {
        path: "/app",
        element: (
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <Navigate to="/app/contacts" replace /> },
          { path: "contacts", element: <ContactsList /> },
          { path: "contacts/export", element: <ExportPage /> },
          { path: "contacts/new", element: <ContactNew /> },
          { path: "contacts/import", element: <ContactImport /> },
          { path: "contacts/collect", element: <CollectContacts /> },
          { path: "contacts/:id", element: <ContactDetail /> },
          { path: "contacts/:id/edit", element: <ContactEdit /> },
          { path: "contacts/households", element: <HouseholdsList /> },
          { path: "contacts/households/new", element: <HouseholdNew /> },
          { path: "contacts/households/:id", element: <HouseholdDetailPage /> },
          { path: "contacts/households/:id/edit", element: <HouseholdEdit /> },
          { path: "profile", element: <ProfilePage /> },
          {
            path: "admin",
            element: (
              <RequireAdmin>
                <Outlet />
              </RequireAdmin>
            ),
            children: [
              { index: true, element: <AdminDashboard /> },
              { path: "users", element: <AdminUsersList /> },
              { path: "users/new", element: <AdminUserNew /> },
              { path: "users/:id", element: <AdminUserDetail /> },
            ],
          },
        ],
      },
      {
        // Auth pages a logged-in user shouldn't see: bounce them into the app.
        // Recover/reset stay reachable (an authed user may follow an emailed
        // reset link), so they live outside this group.
        element: (
          <RedirectIfAuthed>
            <Outlet />
          </RedirectIfAuthed>
        ),
        children: [
          { path: "/auth/login", element: <Login /> },
          { path: "/auth/signup", element: <Signup /> },
        ],
      },
      { path: "/auth/recover", element: <PasswordRecover /> },
      { path: "/auth/reset/:token", element: <PasswordReset /> },
      { path: "/share/:token", element: <CollectShare /> },
    ],
  },
]);
